import {
    DEFAULT_SENDER_MAIL_ADDRESS,
    ERDS_DEFAULT_SUPPLIER,
    Language,
    TaskType,
    TaskValidationStatus,
} from '@eservices/shared/constant';
import { API } from '@eservices/shared/type';
import { Tabs } from 'antd';
import Form, { useForm } from 'antd/lib/form/Form';
import React, { useEffect, useRef, useState } from 'react';
import EmailEditor from 'react-email-editor';
import { FormItem } from '../../../components/FormItem';
import { useTranslation } from '../../../hooks/translationHook';
import { useUploadBlob } from '../../../hooks/uploadBlobHook';
import { TaskDetailProps } from '../../../types';
import { getDaysFrom } from '../../../utils/helpers';
import EmailTemplate from '../common/EmailTemplate';
import TaskActions from '../common/TaskActions';
import TaskDangerousActions from '../common/TaskDangerousActions';
import { convertFormToErdsTask, ErdsTaskFormFields } from './formHelpers';
import SendingConfiguration from './SendingConfiguration';

const { TabPane } = Tabs;

const tabConfiguration = 'configuration';
const tabTemplate = 'template';

const defaultFormValues: ErdsTaskFormFields = {
    taskType: TaskType.ERDS,
    name: '',
    supplierId: ERDS_DEFAULT_SUPPLIER,
    timeToWait: 1,
    mergeDocuments: false,
    validationStatus: TaskValidationStatus.PENDING,
    sender: {
        displayName: '',
        emailAddress: DEFAULT_SENDER_MAIL_ADDRESS,
    },
    subject: '',
    defaultLanguage: Language.EN,
};

interface ErdsTaskConfigurationProps extends TaskDetailProps<API.TaskERDS> {
    workflow: API.WorkflowConfiguration;
    placeholders: string[];
}

const ErdsTaskConfiguration: React.FC<ErdsTaskConfigurationProps> = ({
    order,
    task,
    companyId,
    workflow,
    isReadOnly,
    onSave,
    onCancel,
    onError,
    onDelete,
    allowedToSave,
    placeholders,
}) => {
    const { t, tValidation } = useTranslation('workflow');
    const [isLoading, setIsLoading] = useState(false);
    const { uploadBlob: uploadHTMLTemplate } = useUploadBlob(companyId);
    const { uploadBlob: uploadJSONTemplate } = useUploadBlob(companyId);
    const emailEditorRef = useRef<EmailEditor>();
    const [form] = useForm<ErdsTaskFormFields>();

    useEffect(() => {
        if (task) {
            form.setFieldsValue({
                ...task,
                timeToWait: getDaysFrom(task.maxDurationInSeconds),
            });
        }
    }, []);

    const saveTask = async () => {
        try {
            setIsLoading(true);
            const taskFields = await form.validateFields();

            if (emailEditorRef.current) {
                const { bodyTemplate, bodyJsonTemplate } = await new Promise((resolve) => {
                    emailEditorRef.current.editor.exportHtml(async (data: { html: string; design: string }) => {
                        const { html, design } = data;

                        if (design === undefined) {
                            throw new Error(tValidation('template'));
                        }

                        const filenamePrefix = `${workflow.id}-${new Date().toISOString()}`;

                        const [bodyTemplate, bodyJsonTemplate] = await Promise.all([
                            uploadHTMLTemplate(`${filenamePrefix}.html`, html),
                            uploadJSONTemplate(`${filenamePrefix}.json`, JSON.stringify(design)),
                        ]);
                        resolve({ bodyTemplate, bodyJsonTemplate });
                    });
                });
                if (bodyTemplate.url === undefined) {
                    throw Error(tValidation('template'));
                } else {
                    const task = convertFormToErdsTask(taskFields, bodyTemplate, bodyJsonTemplate);
                    onSave(order, task);
                }
            } else {
                throw Error(tValidation('template'));
            }
        } catch (e) {
            let errors = [];
            if (e?.errorFields) {
                errors = e?.errorFields.map(({ errors }) => errors.join(', '));
            } else {
                errors.push(e.message);
            }
            onError(errors);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Form
            form={form}
            initialValues={defaultFormValues}
            layout="vertical"
            name="erdsTaskConfiguration"
            requiredMark={false}
        >
            <FormItem name="validationStatus" hidden />
            <FormItem name="id" hidden />
            <FormItem name="supplierId" hidden />
            <FormItem name="taskType" hidden />
            <FormItem hidden name="mergeDocuments" />
            <FormItem hidden name={['attachment', 'url']} />
            <FormItem hidden name={['attachment', 'filename']} />
            <FormItem hidden name={['htmlTemplate', 'url']} />
            <FormItem hidden name={['htmlTemplate', 'filename']} />
            <Tabs
                defaultActiveKey={tabConfiguration}
                tabBarExtraContent={
                    <TaskActions
                        isLoading={isLoading}
                        onCancel={onCancel}
                        onSave={saveTask}
                        allowedToSave={allowedToSave && !isLoading}
                        taskType={TaskType.ERDS}
                        isReadOnly={isReadOnly}
                    />
                }
            >
                <TabPane tab={t(tabConfiguration)} key={tabConfiguration}>
                    <SendingConfiguration isReadOnly={isReadOnly} form={form} inputSource={workflow.inputSource} />
                </TabPane>
                <TabPane tab={t(tabTemplate)} key={tabTemplate} forceRender={true}>
                    <EmailTemplate
                        ref={emailEditorRef}
                        placeholders={placeholders}
                        bodyJsonTemplateUrl={task?.bodyJsonTemplate?.url}
                        setLoading={(isLoading) => setIsLoading(isLoading)}
                    />
                </TabPane>
            </Tabs>
            <TaskDangerousActions canBeDeleted={task !== undefined && !isReadOnly} onDelete={onDelete} />
        </Form>
    );
};

export default ErdsTaskConfiguration;
